import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: "What is the Mirror Effect?",
      answer: "The Mirror Effect describes how AI systems reflect and amplify human behavioral patterns, including biases and emotional inconsistencies. Rather than filtering these patterns, current AI systems mirror them back—often making them worse."
    },
    {
      question: "What is Constitutional AI?",
      answer: "Constitutional AI is a framework that embeds explicit normative constraints directly into AI systems. Instead of relying solely on learning from human feedback, it uses a 'constitution'—a set of rules and principles—to guide AI behavior, creating auditable safety guarantees."
    },
    {
      question: "Can AI companies be held legally liable for behavioral mimicry?",
      answer: "Yes. Under product liability law, behavioral mimicry can be classified as a design defect. If a safer alternative design exists (like Constitutional AI), manufacturers can be held strictly liable for resulting harms."
    },
    {
      question: "How can states implement Constitutional AI standards?",
      answer: "States can update procurement requirements, require Constitutional AI disclosures, establish insurance requirements that incentivize Safe RLHF adoption, and create 'digital recall' authority for AI systems that fail safety audits."
    },
    {
      question: "What is AI sycophancy?",
      answer: "AI sycophancy is the tendency of AI systems to agree with users regardless of accuracy—the 'Yes-Man' design defect. In high-stakes contexts like medical diagnosis or legal advice, this can lead to catastrophic outcomes by reinforcing user errors."
    },
    {
      question: "How does this relate to NIST AI RMF?",
      answer: "The NIST AI Risk Management Framework provides the governance structure for implementing Constitutional AI. It can mandate constitutional constraints as policy and audit compliance with Safe RLHF standards."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate-600">
            Quick answers to common questions about Constitutional AI and the Mirror Effect
          </p>
        </div>

        {/* FAQ List */}
        <div className="space-y-4 mb-12">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-start justify-between gap-4 text-left cursor-pointer hover:bg-slate-50 transition-colors duration-200"
              >
                <h3 className="text-lg font-bold text-slate-900 flex-1">
                  {faq.question}
                </h3>
                <div className="flex items-center justify-center w-8 h-8 flex-shrink-0">
                  <i className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-2xl text-slate-600 transition-transform duration-300`}></i>
                </div>
              </button>
              
              {openIndex === index && (
                <div className="px-6 pb-6 pt-2 border-t border-slate-100">
                  <p className="text-slate-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* View All Link */}
        <div className="text-center">
          <a 
            href="/faq"
            className="inline-flex items-center gap-2 text-red-800 hover:text-red-900 font-semibold text-lg transition-colors duration-200 cursor-pointer"
          >
            View All FAQs
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
